import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import { Link } from 'react-router-dom';
import {
  Wallet,
  Plus,
  Trash2,
  RefreshCw,
  Loader2,
  TrendingUp,
  X,
  Info,
  Copy,
  Check,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getApiErrorMessage } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const Accounts = () => {
  const { api, user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [company, setCompany] = useState(null);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    currency: 'AUD',
    account_type: 'standard',
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    const fetchDeposits = async () => {
      try {
        const res = await api.get('/banking/transactions?type=deposit&per_page=10');
        const tx = res.data?.data || res.data?.transactions || res.data?.items || [];
        setRecentDeposits(Array.isArray(tx) ? tx : []);
      } catch {
        setRecentDeposits([]);
      }
    };
    fetchDeposits();
  }, [api]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/company-information');
        setCompany(res.data);
      } catch {
        setCompany({ entity_name: 'Your Company' });
      }
    };
    load();
  }, [api]);

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const res = await api.get('/gateway/supported-currencies');
        setSupportedCurrencies(res.data?.data || []);
      } catch {
        setSupportedCurrencies([]);
      }
    };
    loadCurrencies();
  }, [api]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await api.post('/accounts', formData);
      toast.success('Account created successfully');
      setShowCreate(false);
      setFormData({ name: '', currency: 'AUD', account_type: 'standard' });
      fetchAccounts();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to create account'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await api.delete(`/accounts/${accountId}`);
      toast.success('Account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to delete account'));
    }
  };

  const formatCurrency = (amount, currency) => {
    const num = parseFloat(amount);
    if (currency === 'USDC' || currency === 'USDT') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })} ${currency}`;
    }
    const locale = currency === 'IDR' ? 'id-ID' : currency === 'PHP' ? 'en-PH' : currency === 'THB' ? 'th-TH' : currency === 'VND' ? 'vi-VN' : currency === 'MYR' ? 'ms-MY' : 'en-AU';
    const fractionDigits = (currency === 'IDR' || currency === 'VND') ? 0 : 2;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: fractionDigits,
    }).format(num);
  };

  const getCurrencyIcon = (currency) => {
    const match = supportedCurrencies.find((c) => c.code === currency);
    if (match) return match.flag;
    const icons = { AUD: '🇦🇺', USD: '🇺🇸', EUR: '🇪🇺', IDR: '🇮🇩', PHP: '🇵🇭', THB: '🇹🇭', MYR: '🇲🇾', VND: '🇻🇳', USDC: '₿', USDT: '₮' };
    return icons[currency] || '💰';
  };

  const getCurrencyClass = (currency) => {
    const classes = {
      AUD: 'bg-[#E6F7F3] border-[#A5E0D2]',
      USD: 'bg-blue-50 border-blue-200',
      EUR: 'bg-purple-50 border-purple-200',
      IDR: 'bg-red-50 border-red-200',
      PHP: 'bg-yellow-50 border-yellow-200',
      THB: 'bg-indigo-50 border-indigo-200',
      MYR: 'bg-teal-50 border-teal-200',
      VND: 'bg-orange-50 border-orange-200',
      USDC: 'bg-cyan-50 border-cyan-200',
      USDT: 'bg-emerald-50 border-emerald-200',
    };
    return classes[currency] || 'bg-slate-50 border-slate-200';
  };

  const getGatewayLabel = (account) => {
    const gw = account.gateway;
    if (gw === 'xendit') return 'Xendit';
    if (gw === 'payitnow') return 'PayItNow';
    return null;
  };

  const totalBalance = accounts.reduce((sum, acc) => {
    const rate = acc.currency === 'USD' ? 1.5 : acc.currency === 'EUR' ? 1.7 : acc.currency === 'IDR' ? 0.000095 : acc.currency === 'PHP' ? 0.027 : acc.currency === 'THB' ? 0.044 : acc.currency === 'MYR' ? 0.34 : acc.currency === 'VND' ? 0.000061 : 1;
    return sum + parseFloat(acc.balance) * rate;
  }, 0);

  const canManageAccounts = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'merchant_admin';

  const copyAccountDetails = (account, opts = {}) => {
    const { displayBSB, displayAccountNumber } = opts;
    const parts = [];
    if (displayBSB && displayBSB !== 'N/A') parts.push(`BSB: ${displayBSB}`);
    if (displayAccountNumber) {
      parts.push(account.wallet_address ? `Wallet: ${displayAccountNumber}` : `Account: ${displayAccountNumber}`);
    }
    const text = [account.name, account.currency, ...parts].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text);
    setCopiedId(account.id);
    toast.success('Account details copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const lastSynced = new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'medium' });

  return (
    <Layout>
      <div data-testid="accounts-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0F172A] ">
              Accounts
            </h1>
            <p className="text-slate-500 mt-1">Manage your multi-currency accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchAccounts} variant="outline" className="h-11 px-5 rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canManageAccounts && (
              <Button 
                onClick={() => setShowCreate(true)}
                className="h-11 px-6 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white"
                data-testid="create-account-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Account
              </Button>
            )}
          </div>
        </div>

        {/* Account Name & Total Portfolio - side by side, equal boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-[#1C472B] text-white rounded-2xl border-0 shadow-sm" data-testid="company-name-card">
            <CardContent className="p-6 h-full flex flex-col justify-between min-h-[140px]">
              <div>
                <p className="text-[#A5E0D2] text-sm mb-1">Account Name</p>
                <p className="text-2xl font-bold ">
                  {(company?.entity_name || 'Your Company').toUpperCase()}
                </p>
                {company?.address && (
                  <p className="text-slate-300 text-sm mt-2">{company.address}</p>
                )}
              </div>
              <div className="text-[#A5E0D2] text-sm mt-4">
                <p>Last Synced</p>
                <p className="text-white font-semibold">{lastSynced}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1C472B] text-white rounded-2xl border-0 shadow-sm" data-testid="total-balance-card">
            <CardContent className="p-6 h-full flex flex-col justify-between min-h-[140px]">
              <div>
                <p className="text-slate-300 text-sm mb-1">Total Portfolio Value</p>
                <p className="text-3xl font-extrabold ">
                  {new Intl.NumberFormat('en-AU', {
                    style: 'currency',
                    currency: 'AUD',
                    minimumFractionDigits: 2,
                  }).format(totalBalance)}
                </p>
                <p className="text-slate-300 text-sm mt-2">
                  Across {accounts.length} accounts
                </p>
              </div>
              <div className="flex justify-end mt-4">
                <div className="w-16 h-16 bg-[#A5E0D2] rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-[#1C472B]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Accounts Grid - extra spacing for logical separation */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#1C472B]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {accounts.map((account, idx) => {
              const hasVA = Boolean(account.virtual_account_number);
              const hasRealAccountNumber = Boolean(account.account_number || account.wallet_address || hasVA);
              const dummyAccountNumber = `•••• ${String(9870 + (idx % 100)).padStart(4, '0')}`;
              const displayAccountNumber = hasVA
                ? account.virtual_account_number
                : hasRealAccountNumber
                  ? (account.account_number || account.wallet_address)
                  : dummyAccountNumber;
              const hasRealBSB = Boolean(account.bsb);
              const dummyBSB = '000-000';
              const isAUD = account.currency === 'AUD';
              const isXendit = account.gateway === 'xendit';
              const displayBSB = isAUD ? (hasRealBSB ? account.bsb : dummyBSB) : 'N/A';
              return (
                <Card
                key={account.id}
                className={`rounded-2xl border-2 ${getCurrencyClass(account.currency)} shadow-sm`}
                data-testid={`account-${account.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-2xl shadow-sm">
                        {getCurrencyIcon(account.currency)}
                      </div>
                      <div>
                        <p className="font-semibold text-[#0F172A]">{account.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-500 capitalize">{account.account_type}</p>
                          {getGatewayLabel(account) && (
                            <span className="text-[10px] font-medium text-slate-400 bg-white/80 border border-slate-200 rounded px-1.5 py-0.5">
                              via {getGatewayLabel(account)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {user?.role === 'admin' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4 text-[#F08080]" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. The account and all associated data will be permanently deleted.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteAccount(account.id)}
                              className="bg-[#F08080] hover:bg-[#E07070] text-white"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>

                  <div className="mt-6">
                    <p className="text-sm text-slate-500">Balance</p>
                    <p className="text-2xl font-bold text-[#0F172A] ">
                      {formatCurrency(account.balance, account.currency)}
                    </p>
                  </div>

                  {/* Account number / Wallet / VA - BSB same row for AUD only */}
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 flex flex-wrap items-start gap-x-4 gap-y-3">
                      {isAUD && (
                        <div>
                          <p className="text-xs text-slate-500 mb-0.5">BSB</p>
                          <p className="font-mono font-semibold text-[#0F172A] text-sm">{displayBSB}</p>
                        </div>
                      )}
                      {isXendit && account.bank_code && (
                        <div>
                          <p className="text-xs text-slate-500 mb-0.5">Bank</p>
                          <p className="font-mono font-semibold text-[#0F172A] text-sm">{account.bank_code}</p>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-500 mb-0.5">
                          {hasVA ? 'Virtual Account' : account.wallet_address ? 'Wallet' : 'Account Number'}
                        </p>
                        <p className="font-mono font-semibold text-[#0F172A] truncate" data-testid={`account-number-${account.id}`}>
                          {displayAccountNumber}
                        </p>
                      </div>
                      {account.deposit_reference && (
                        <div>
                          <p className="text-xs text-slate-500 mb-0.5">Deposit Ref</p>
                          <p className="font-mono font-semibold text-[#0F172A] text-xs bg-slate-100 px-1.5 py-0.5 rounded">{account.deposit_reference}</p>
                        </div>
                      )}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="p-2 rounded-lg hover:bg-[#E6F7F3] transition-colors shrink-0" title="View complete details">
                          <Info className="w-5 h-5 text-[#1C472B]" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-4" align="end">
                        <p className="font-semibold text-slate-800 mb-3">Complete account details</p>
                        <div className="space-y-2 text-sm text-slate-600">
                          <p><strong>Name:</strong> {account.name}</p>
                          <p><strong>Currency:</strong> {account.currency}</p>
                          <p><strong>Type:</strong> {account.account_type}</p>
                          {isAUD && <p><strong>BSB:</strong> {displayBSB}</p>}
                          <p><strong>{account.wallet_address ? 'Wallet' : 'Account Number'}:</strong> {displayAccountNumber}</p>
                          {account.virtual_account_number && (
                            <p><strong>Virtual Account:</strong> {account.virtual_account_number}</p>
                          )}
                          {account.internal_va_number && (
                            <p><strong>Internal VA:</strong> {account.internal_va_number}</p>
                          )}
                          {account.deposit_reference && (
                            <p className="text-xs"><strong>Deposit Ref:</strong> <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{account.deposit_reference}</span></p>
                          )}
                          {account.bank_code && (
                            <p><strong>Bank:</strong> {account.bank_code}</p>
                          )}
                          <p><strong>Balance:</strong> {formatCurrency(account.balance, account.currency)}</p>
                          <p><strong>Status:</strong> {account.status}</p>
                          {getGatewayLabel(account) && (
                            <p><strong>Gateway:</strong> {getGatewayLabel(account)}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="mt-4 w-full bg-[#1C472B] hover:bg-[#143620] text-white"
                          onClick={() => copyAccountDetails(account, { displayBSB, displayAccountNumber })}
                        >
                          {copiedId === account.id ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                          {copiedId === account.id ? 'Copied' : 'Copy account details'}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        account.status === 'active'
                          ? 'bg-[#E6F7F3] text-[#1C472B]'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {account.status}
                    </span>
                    <span className="text-xs text-slate-400">
                      {account.currency}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}

        {/* Recent Deposits - dummy data synced with account balances / PIN when available */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="recent-deposits">
          <CardHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-4">
            <CardTitle className="text-lg font-semibold text-[#0F172A] ">
              Recent Deposits
            </CardTitle>
            <Link
              to="/transactions?type=deposit"
              className="text-[#1C472B] font-semibold hover:underline flex items-center gap-1 text-sm"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-2">Date</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-2">Reference</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-2">Counterparty</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-2">Type</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDeposits.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-400 text-sm">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    recentDeposits.map((tx) => (
                      <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-4 px-2 text-sm text-slate-600">
                          {new Date(tx.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="py-4 px-2 text-sm font-mono text-[#0F172A]">{tx.reference || '—'}</td>
                        <td className="py-4 px-2 text-sm text-slate-700">{tx.counterparty || '—'}</td>
                        <td className="py-4 px-2 text-sm capitalize text-slate-600">{tx.type || 'deposit'}</td>
                        <td className="py-4 px-2 text-sm text-right font-semibold text-[#1C472B]">
                          +{formatCurrency(tx.amount || '0', tx.currency || 'AUD')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Supported Currencies Info */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <CardHeader className="px-6 pt-6 pb-4">
            <CardTitle className="text-lg font-semibold text-[#0F172A] ">
              Supported Currencies
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { code: 'AUD', name: 'Australian Dollar', icon: '🇦🇺' },
                { code: 'USD', name: 'US Dollar', icon: '🇺🇸' },
                { code: 'EUR', name: 'Euro', icon: '🇪🇺' },
                { code: 'USDC', name: 'USD Coin', icon: '₿' },
                { code: 'USDT', name: 'Tether', icon: '₮' },
              ].map((currency) => (
                <div
                  key={currency.code}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl"
                >
                  <span className="text-2xl">{currency.icon}</span>
                  <div>
                    <p className="font-semibold text-[#0F172A]">{currency.code}</p>
                    <p className="text-xs text-slate-500">{currency.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Create Account Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowCreate(false)}
            />
            
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-fade-in">
              <div className="px-8 pt-8 pb-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0F172A] ">
                      Create Account
                    </h2>
                    <p className="text-slate-500 mt-1">Add a new currency account</p>
                  </div>
                  <button
                    onClick={() => setShowCreate(false)}
                    className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateAccount} className="px-8 py-6 space-y-5">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Account Name
                  </Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My USD Account"
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1C472B] focus:ring-1 focus:ring-[#1C472B]"
                    required
                    data-testid="account-name"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Currency
                  </Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => {
                      const match = supportedCurrencies.find((c) => c.code === value);
                      const atype = match?.account_type || (value === 'USDC' || value === 'USDT' ? 'crypto' : 'standard');
                      setFormData({ ...formData, currency: value, account_type: atype });
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="account-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {supportedCurrencies.length > 0 ? (
                        supportedCurrencies.filter((c) => c.available).map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.name} ({c.code})
                            {c.gateway !== 'local' && <span className="text-xs text-slate-400 ml-1">via {c.gateway === 'xendit' ? 'Xendit' : c.gateway === 'payitnow' ? 'PayItNow' : c.gateway}</span>}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="AUD">🇦🇺 Australian Dollar (AUD)</SelectItem>
                          <SelectItem value="USD">🇺🇸 US Dollar (USD)</SelectItem>
                          <SelectItem value="EUR">🇪🇺 Euro (EUR)</SelectItem>
                          <SelectItem value="IDR">🇮🇩 Indonesian Rupiah (IDR)</SelectItem>
                          <SelectItem value="USDC">₿ USD Coin (USDC)</SelectItem>
                          <SelectItem value="USDT">₮ Tether (USDT)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Account Type
                  </Label>
                  <Select
                    value={formData.account_type}
                    onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="account-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                      <SelectItem value="crypto">Crypto Wallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreate(false)}
                    className="flex-1 h-12 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white font-semibold"
                    disabled={createLoading}
                    data-testid="submit-account"
                  >
                    {createLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Accounts;
